/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, breakpoints } from '@coursera/cds-core';

import AuthenticationV3CtaTracking from 'bundles/authentication-common/components/AuthenticationV3CtaTracking';
import LinkButton from 'bundles/authentication-common/components/LinkButton';
import Separator from 'bundles/authentication-common/components/Separator';
import type { OnAuthenticationOptions } from 'bundles/authentication-common/components/actions';
import SocialSSO from 'bundles/authentication-common/components/authenticationModal/SocialSSO';
import RecaptchaDisclaimer from 'bundles/authentication-common/components/botManager/RecaptchaDisclaimer';
import SignupForm, { InputFields } from 'bundles/authentication-common/components/forms/SignupForm';
import type SSOError from 'bundles/authentication-common/components/sso/SSOError';
import {
  AUTHENTICATION_FORM_VARIANTS,
  type AUTHENTICATION_FORM_VARIANTS_KEY,
  AUTHENTICATION_MODAL_MODES,
  type AUTHENTICATION_MODAL_MODES_KEY,
  LEARNER_HELP_CENTER_LINK,
} from 'bundles/authentication-common/constants';
import type { EnterpriseSsoLoginInfo } from 'bundles/authentication-common/hooks/useEnterpriseSsoCheck';
import type { ThirdPartyOrganizationsData } from 'bundles/authentication-common/hooks/useUserThirdPartyOrganizationsQuery';
import type { SuccessResponse } from 'bundles/authentication-common/types';
import type { ArkoseBotManagerContext } from 'bundles/authentication-common/types/BotManagerTypes';
import { shouldShowLegalConsolidationAuthFlow, shouldShowTOSDisclaimer } from 'bundles/authentication-common/utils';
import { SSO_VARIANT, completeLtiLaunchIfPending, getSSOVariant } from 'bundles/authentication-common/utils/ssoUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import useSaveOnboardingOnSignup from 'bundles/onboarding-common/hooks/useSaveOnboardingOnSignup';
import SSOButton from 'bundles/user-account/components/SsoButton';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/authentication';

export type Props = {
  defaultEmail?: string;
  className?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  onSignup: (redirectURL: string, options?: OnAuthenticationOptions) => void;
  onSSOSuccess: (isRegistration: boolean, hasSavedOnboardingOnSignup?: boolean) => void;
  onSSOError: (error: SSOError) => void;
  title?: string;
  tagline?: string;
  arkoseBotManagerContext?: ArkoseBotManagerContext;
  isEnterprise?: boolean;
  verificationEmailRedirectUrl?: string;
  shouldShowCustomSSO?: boolean;
  forceSSOLogin?: boolean;
  authProvider?: string;
  hideOrganizationSSO?: boolean;
  formVariant?: AUTHENTICATION_FORM_VARIANTS_KEY;
  areAnyApiRequestsInFlight?: boolean;
  ssoError?: SSOError;
  resetSsoError?: () => void;
  enterpriseSsoEmailCheckInfo?: {
    enterpriseSsoLoginInfo: EnterpriseSsoLoginInfo;
    onContinueWithSso: () => void;
    onEmailChange: (email: string) => void;
    resetEnterpriseSsoInfo: () => void;
  };
  showProgressiveAuth?: boolean;
} & ThirdPartyOrganizationsData;

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);

const styles = {
  container: css`
    display: grid;
    grid-gap: var(--cds-spacing-300);
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: 0;

    @media (min-width: 600px) {
      padding: var(--cds-spacing-50);
    }

    .cds-text-field-root {
      margin-top: var(--cds-spacing-50) !important;
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-200);
    }
  `,
  tagLine: css`
    margin: var(--cds-spacing-50) auto 0;
    width: 100%;
  `,
  title: css`
    margin: 0;
  `,
  navigation: css`
    margin: var(--cds-spacing-200) 0 0;
  `,
  link: css`
    text-decoration: underline;
  `,
  organization: css`
    margin: 0;
  `,
  separator: css`
    margin: 12px 0 !important;
  `,
  terms: css`
    margin: var(--cds-spacing-50) 0 0;
    padding: 0;

    &:first-of-type {
      margin: 0;
    }

    &:nth-of-type(n) a {
      color: inherit;
      text-decoration: underline;
    }
  `,
};

const Signup = ({
  defaultEmail,
  navigate,
  onSignup,
  onSSOSuccess,
  onSSOError,
  className,
  title,
  tagline,
  arkoseBotManagerContext,
  isEnterprise,
  verificationEmailRedirectUrl,
  shouldShowCustomSSO,
  forceSSOLogin,
  organizations,
  authProvider,
  hideOrganizationSSO,
  areAnyApiRequestsInFlight,
  ssoError,
  resetSsoError,
  enterpriseSsoEmailCheckInfo,
  formVariant,
  showProgressiveAuth,
}: Props) => {
  const { saveOnboardingOnSignup } = useSaveOnboardingOnSignup();

  const navigateToLogin = () => {
    navigate(AUTHENTICATION_MODAL_MODES.LOGIN);
  };

  const navigateToOrganizationSSO = () => {
    navigate(AUTHENTICATION_MODAL_MODES.ORGANIZATIONSIGNUP, { referrer: AUTHENTICATION_MODAL_MODES.SIGNUP });
  };

  const handleSuccess = async (response: SuccessResponse) => {
    Instrumentation.register();

    const hasSavedOnboardingOnSignup = await saveOnboardingOnSignup();
    onSignup(response.redirectTo, {
      addShowTouAcceptParam: shouldShowLegalConsolidationAuthFlow(),
      hasSavedOnboardingOnSignup,
    });
  };

  const handleSSOSuccess = async (isRegistration: boolean) => {
    await completeLtiLaunchIfPending(ssoError);
    const hasSavedOnboardingOnSignup = await saveOnboardingOnSignup();
    onSSOSuccess(isRegistration, hasSavedOnboardingOnSignup);
  };

  const hideForm = organizations?.[0]?.supportsDegreeSSOLogin || forceSSOLogin;

  const ssoVariant = getSSOVariant({ authProvider, isEnterprise });

  const shouldHideSocialSSO = formVariant === AUTHENTICATION_FORM_VARIANTS.PASSWORD;

  return (
    <section css={styles.container} className={className} data-catchpoint="authentication-box-content">
      <header>
        <Typography2 component="h2" variant="titleXSmall" align="left" css={styles.title}>
          {title ?? _t('Sign up')}
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" color="supportText" align="left" css={styles.tagLine}>
          {tagline ?? (
            <FormattedMessage message={_t('Learn on your own time from top \n universities and businesses.')} />
          )}
        </Typography2>
      </header>
      <div>
        {/* TODO: Consolidate the whitelist organization for SSO login logic to `getSSOVariant` utility */}
        {authProvider && (areAnyApiRequestsInFlight || shouldShowCustomSSO) && (
          <>
            <SSOButton provider={authProvider} organizations={organizations} loading={areAnyApiRequestsInFlight} />
            {!areAnyApiRequestsInFlight && !forceSSOLogin && <Separator css={styles.separator} text={_t('or')} />}
          </>
        )}
        {!areAnyApiRequestsInFlight && !hideForm && (
          <SignupForm
            defaultEmail={defaultEmail}
            navigate={navigate}
            navigateToLogin={navigateToLogin}
            onSuccess={handleSuccess}
            verificationEmailRedirectUrl={verificationEmailRedirectUrl}
            arkoseBotManagerContext={arkoseBotManagerContext}
            enterpriseSsoProps={{
              ssoError,
              resetSsoError,
              isEnterpriseSsoLogin: enterpriseSsoEmailCheckInfo?.enterpriseSsoLoginInfo?.isEnterpriseSsoLogin,
              isLoading: enterpriseSsoEmailCheckInfo?.enterpriseSsoLoginInfo?.isLoading,
              isRedirecting: enterpriseSsoEmailCheckInfo?.enterpriseSsoLoginInfo?.isRedirecting,
              onEmailChange: enterpriseSsoEmailCheckInfo?.onEmailChange,
              onSubmit: enterpriseSsoEmailCheckInfo?.onContinueWithSso,
            }}
            inputFieldOrdering={
              showProgressiveAuth ? [InputFields.EMAIL, InputFields.NAME, InputFields.PASSWORD] : undefined
            }
          />
        )}
        {ssoVariant === SSO_VARIANT.SOCIAL && !shouldHideSocialSSO && (
          <>
            <Separator css={styles.separator} text={_t('or')} customStyles={{ margin: '22px 0' }} />
            <SocialSSO onError={onSSOError} onSuccess={handleSSOSuccess} />
          </>
        )}
        <Typography2
          component="p"
          variant="bodySecondary"
          align="left"
          css={styles.navigation}
          className="auth-form-type-navigation"
        >
          <FormattedMessage
            message={_t('Already on Coursera? {loginLink}')}
            loginLink={
              <AuthenticationV3CtaTracking>
                {(trackV3) => {
                  const handleOnClick = () => {
                    trackV3({ name: 'login' });
                    navigateToLogin();
                  };
                  return (
                    <TrackedLinkButton onClick={handleOnClick} trackingName="login" css={styles.link}>
                      {_t('Log in')}
                    </TrackedLinkButton>
                  );
                }}
              </AuthenticationV3CtaTracking>
            }
          />
        </Typography2>
        {ssoVariant === SSO_VARIANT.SOCIAL && !hideOrganizationSSO && (
          <>
            <Separator css={styles.separator} customStyles={{ margin: '12px 0' }} />
            <Typography2 component="p" variant="bodySecondary" align="left" css={styles.organization}>
              <AuthenticationV3CtaTracking>
                {(trackV3) => {
                  const handleOnClick = () => {
                    trackV3({ name: 'signup_with_other_sso' });
                    navigateToOrganizationSSO();
                  };
                  return (
                    <TrackedLinkButton
                      onClick={handleOnClick}
                      trackingName="sign_up_with_organization_btn_click"
                      css={styles.link}
                    >
                      {_t('Sign up with your organization')}
                    </TrackedLinkButton>
                  );
                }}
              </AuthenticationV3CtaTracking>
            </Typography2>
          </>
        )}
      </div>
      <div className="auth-form-tos-section">
        <Typography2 component="p" variant="bodyTertiary" color="supportText" align="left" css={styles.terms}>
          {shouldShowTOSDisclaimer() && (
            <FormattedMessage
              message={_t("I accept Coursera's {tosLink} and {privacyLink}. ")}
              privacyLink={
                <a href="/about/privacy" target="_blank">
                  {_t('Privacy Notice')}
                </a>
              }
              tosLink={
                <a href="/about/terms" target="_blank">
                  {_t('Terms of Use')}
                </a>
              }
            />
          )}
          <FormattedMessage
            message={_t('Having trouble logging in? {helpCenterLink}')}
            helpCenterLink={
              <a href={LEARNER_HELP_CENTER_LINK} target="_blank" rel="noopener noreferrer">
                {_t('Learner help center')}
              </a>
            }
          />
        </Typography2>
        {!areAnyApiRequestsInFlight && !forceSSOLogin && (
          <RecaptchaDisclaimer
            isEnterprise
            variant="bodyTertiary"
            color="supportText"
            align="left"
            css={styles.terms}
          />
        )}
      </div>
    </section>
  );
};

export default Signup;
